/**
 * DataSyncSection — read-only summary of the latest warehouse replication for
 * connectors that replicate to the Klaris-internal Redshift warehouse
 * (Airtable today; HubSpot/Shopify/Drive next). Renders nothing for
 * non-replicating connectors.
 *
 * Data sync is driven exclusively by the unified Build Data Model orchestrator
 * (replicate → refresh schema → profile → ontology); this card no longer
 * triggers syncs on its own. Surfaces:
 *   - Current status pill (never synced / pending / running / success / failed)
 *   - Last successful sync timestamp + rows + tables loaded
 *   - Inline error text on failure
 *
 * Polls /sync-jobs/latest every 3s while a sync is in progress so the user
 * sees live progress without a page refresh.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import CloudSyncRounded from '@mui/icons-material/CloudSyncRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import ErrorRounded from '@mui/icons-material/ErrorRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import { buildApiUrl } from '../../config';
import { formatRelativeTime } from '../../utils/date';

type SyncStatus = 'pending' | 'running' | 'success' | 'failed';

interface DataSyncJob {
  job_id: string;
  status: SyncStatus;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  rows_loaded: number | null;
  tables_synced: number | null;
  progress: any;
  error_message: string | null;
}

interface Props {
  tenantId: string;
  connectorId: string;
  /** Triggered after a successful sync so the parent can refresh connector data. */
  onSyncCompleted?: () => void;
}

const POLL_INTERVAL_MS = 3000;

const DataSyncSection: React.FC<Props> = ({ tenantId, connectorId, onSyncCompleted }) => {
  const [latestJob, setLatestJob] = useState<DataSyncJob | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const pollRef = useRef<number | null>(null);

  const fetchLatest = useCallback(async () => {
    const token = window.localStorage.getItem('klaris_jwt');
    const resp = await fetch(
      buildApiUrl(`/api/v1/tenants/${tenantId}/connectors/${connectorId}/sync-jobs/latest`),
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!resp.ok) {
      setLatestJob(null);
      return null;
    }
    const job = await resp.json();
    setLatestJob(job);
    return job as DataSyncJob | null;
  }, [tenantId, connectorId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLatest().finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchLatest]);

  useEffect(() => {
    if (latestJob?.status !== 'running' && latestJob?.status !== 'pending') {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = window.setInterval(async () => {
      const job = await fetchLatest();
      if (job && job.status !== 'running' && job.status !== 'pending') {
        if (job.status === 'success') onSyncCompleted?.();
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    }, POLL_INTERVAL_MS) as unknown as number;
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [latestJob?.status, fetchLatest, onSyncCompleted]);

  return (
    <Box
      sx={(t) => ({
        mb: 2.5,
        p: 2,
        borderRadius: `${t.ds.radius.sm}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.sunken,
      })}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ minWidth: 0 }}>
          <CloudSyncRounded sx={{ fontSize: 22, color: 'text.secondary' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Data Sync
            </Typography>
            {loading ? (
              <Typography variant="caption" color="text.secondary">
                Loading…
              </Typography>
            ) : (
              <SyncStatusLine job={latestJob} />
            )}
          </Box>
        </Stack>

        <SyncStatusPill status={latestJob?.status ?? null} />
      </Stack>

      {latestJob?.status === 'failed' && latestJob.error_message && (
        <Alert severity="error" sx={{ mt: 1.5 }} icon={<ErrorRounded fontSize="small" />}>
          {latestJob.error_message}
        </Alert>
      )}
    </Box>
  );
};

const SyncStatusPill: React.FC<{ status: SyncStatus | null }> = ({ status }) => {
  if (!status) {
    return <Chip size="small" label="Never synced" variant="outlined" />;
  }
  const map: Record<SyncStatus, { label: string; color: any; icon?: React.ReactElement }> = {
    pending: { label: 'Pending', color: 'default', icon: <ScheduleRounded fontSize="small" /> },
    running: { label: 'Syncing', color: 'info', icon: <CircularProgress size={12} /> },
    success: { label: 'Synced', color: 'success', icon: <CheckCircleRounded fontSize="small" /> },
    failed: { label: 'Failed', color: 'error', icon: <ErrorRounded fontSize="small" /> },
  };
  const { label, color, icon } = map[status];
  return <Chip size="small" label={label} color={color} icon={icon} variant="outlined" />;
};

const SyncStatusLine: React.FC<{ job: DataSyncJob | null }> = ({ job }) => {
  if (!job) {
    return (
      <Typography variant="caption" color="text.secondary">
        No data has been synced yet. Run <b>Build Data Model</b> to replicate this connector into Klaris.
      </Typography>
    );
  }
  if (job.status === 'running' || job.status === 'pending') {
    return (
      <Typography variant="caption" color="text.secondary">
        Started {job.started_at ? formatRelativeTime(job.started_at) : 'just now'}
      </Typography>
    );
  }
  if (job.status === 'success') {
    const completed = job.completed_at ? formatRelativeTime(job.completed_at) : '—';
    const rows = job.rows_loaded ?? 0;
    const tables = job.tables_synced ?? 0;
    return (
      <Typography variant="caption" color="text.secondary">
        Last synced {completed} · {rows.toLocaleString()} rows across {tables} tables
      </Typography>
    );
  }
  return (
    <Typography variant="caption" color="error">
      Last attempt failed {job.completed_at ? formatRelativeTime(job.completed_at) : ''}
    </Typography>
  );
};

export default DataSyncSection;
